// Helpers de apresentação de propostas (Story 11.6).
// Centraliza rótulos/cores de status, formatação de valor e o destaque de
// "validade próxima/vencida" (AC10). Disciplina de timezone: usa helpers de
// @control-center/shared (nada de new Date("YYYY-MM-DD") / toLocale pra datas;
// formatProposalMoney usa toLocaleString só pra moeda, permitido pela regra).

import type { CrmProposalStatusSchema } from '@control-center/shared';
import { addDaysLocal, todayLocal } from '@control-center/shared';

/** Rótulo PT-BR de cada status do ciclo da proposta. Fonte única (lista + detalhe). */
export const PROPOSAL_STATUS_LABELS: Record<CrmProposalStatusSchema, string> = {
  rascunho: 'Rascunho',
  enviada: 'Enviada',
  visualizada: 'Visualizada',
  aceita: 'Aceita',
  recusada: 'Recusada',
  expirada: 'Expirada',
  convertida: 'Convertida',
};

/**
 * Classe de cor do badge por categoria de status (AC10). Usa as cores de estado
 * do design system via tokens utilitários (border/text) — sem cor hardcoded.
 * rascunho=neutro, enviada/visualizada=informativo, aceita=positivo,
 * recusada=negativo, expirada=atenção, convertida=destaque (accent).
 */
export const PROPOSAL_STATUS_BADGE: Record<CrmProposalStatusSchema, string> = {
  rascunho: 'border-line text-muted',
  enviada: 'border-info/40 text-info',
  visualizada: 'border-info/40 text-info',
  aceita: 'border-good/40 text-good',
  recusada: 'border-bad/40 text-bad',
  expirada: 'border-warn/40 text-warn',
  convertida: 'border-accent/40 text-accent',
};

/** Formata valor monetário (string Decimal do backend) em BRL, ou '—' se null. */
export function formatProposalMoney(value: string | null): string {
  if (value === null) return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return value;
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/**
 * Sinaliza se a validade está próxima (<= hoje+3 dias) ou já vencida (AC10).
 * Trabalha em strings YYYY-MM-DD no fuso BR (sem shift UTC).
 */
export type ValidityFlag = 'expired' | 'soon' | 'ok';

export function validityFlag(validUntil: string, today: string = todayLocal()): ValidityFlag {
  if (validUntil < today) return 'expired';
  if (validUntil <= addDaysLocal(today, 3)) return 'soon';
  return 'ok';
}

/** Classe de cor (texto) pra coluna de validade conforme o flag (AC10). */
export function validityClass(flag: ValidityFlag): string {
  switch (flag) {
    case 'expired':
      return 'text-bad';
    case 'soon':
      return 'text-warn';
    default:
      return 'text-muted';
  }
}
